import { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, BarChart2, Package, ChevronDown, Wallet } from 'lucide-react';
import { financeApi, storeApi } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { FinanceResultModeToggle } from '../components/FinanceResultModeToggle';
import { PageHeader } from '../components/ui/PageHeader';

const plnFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
});

function formatPLN(value: number): string {
  return plnFormatter.format(value);
}

/** Return current date as YYYY-MM-DD in Warsaw timezone (not UTC!) */
function warsawToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date());
}

/** Format Date as YYYY-MM-DD using local (non-UTC) components — avoids timezone shift from toISOString() */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const dateTo = warsawToday();
  // First day of current month in Warsaw timezone
  const dateFrom = `${dateTo.slice(0, 4)}-${dateTo.slice(5, 7)}-01`;
  return { dateFrom, dateTo };
}

// Quick date filter presets
function getDatePreset(preset: string): { from: string; to: string } {
  const to = warsawToday();

  switch (preset) {
    case 'today':
      return { from: to, to };
    case 'yesterday': {
      const y = new Date(to + 'T12:00:00'); // noon to avoid DST edge cases
      y.setDate(y.getDate() - 1);
      const yStr = toLocalDateStr(y);
      return { from: yStr, to: yStr };
    }
    case '7d': {
      const d = new Date(to + 'T12:00:00');
      d.setDate(d.getDate() - 6);
      return { from: toLocalDateStr(d), to };
    }
    case '14d': {
      const d = new Date(to + 'T12:00:00');
      d.setDate(d.getDate() - 13);
      return { from: toLocalDateStr(d), to };
    }
    case 'this_month':
      return { from: `${to.slice(0, 4)}-${to.slice(5, 7)}-01`, to };
    case 'last_month': {
      const now = new Date(to + 'T12:00:00');
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: toLocalDateStr(first), to: toLocalDateStr(last) };
    }
    case 'max':
      return { from: '2020-01-01', to };
    default: {
      const def = getDefaultDateRange();
      return { from: def.dateFrom, to: def.dateTo };
    }
  }
}

const DATE_PRESETS = [
  { key: 'today', label: 'Dzisiaj' },
  { key: 'yesterday', label: 'Wczoraj' },
  { key: '7d', label: '7 dni' },
  { key: '14d', label: '14 dni' },
  { key: 'this_month', label: 'Ten miesiąc' },
  { key: 'last_month', label: 'Ost. miesiąc' },
  { key: 'max', label: 'Maksymalnie' },
];

interface DashboardTotals {
  ordersCount: number;
  itemsSold: number;
  revenueGross: number;
  revenueNet: number;
  shippingGross: number;
  grossProfit: number;
  netProfit: number;
  costProducts: number;
  costShipping: number;
  costFees: number;
  costFixed: number;
  costReturns?: number;
  adSpend: number;
  ctr: number;
  roas: number;
  avgOrder: number;
  revenue: number;
}

interface DashboardChart {
  granularity?: 'hour' | 'day';
  timeZone?: string;
  profitAllocation?: 'REVENUE_SHARE_ESTIMATE';
  labels: string[];
  revenue: number[];
  profit: number[];
  orders: number[];
  adSpend: number[];
  costProducts: number[];
  costShipping: number[];
  costTransaction: number[];
  costFixed: number[];
}

interface StoreBreakdownItem {
  tenantId?: string;
  storeId: string;
  storeName: string;
  revenueGross: number;
  shippingGross: number;
  revenueTotalGross: number;
  revenueNet: number;
  ordersCount: number;
  grossProfit: number;
  netProfit: number;
  costProducts: number;
  costShipping: number;
  costFees: number;
  costFixed: number;
  costReturns?: number;
  adSpend: number;
  roas: number;
  percentage: number;
}

interface CodPendingData {
  enabled: boolean;
  delivered?: { count: number; amount: number };
  all?: { count: number; amount: number };
}

interface DashboardData {
  totals: DashboardTotals;
  chart: DashboardChart;
  storeBreakdown?: StoreBreakdownItem[];
  codPending?: CodPendingData;
}

interface TopProduct {
  externalProductId?: string | null;
  sku?: string | null;
  children?: TopProduct[];
  isAdditionalSale?: boolean;
  id: string;
  name: string;
  qty: number;
  revenue: number;
  ordersCount: number;
  costProducts: number;
  grossProfit: number;
  netProfit: number;
  adSpend: number;
  roas: number;
}

interface StoreOption {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// KPI Card emoji + gradient config
// ---------------------------------------------------------------------------
const KPI_CONFIG: {
  emoji: string;
  gradientColors: [string, string];
}[] = [
  { emoji: '💰', gradientColors: ['#10b981', '#4ade80'] },
  { emoji: '📊', gradientColors: ['#3b82f6', '#22d3ee'] },
  { emoji: '💎', gradientColors: ['#a855f7', '#8b5cf6'] },
  { emoji: '🛒', gradientColors: ['#06b6d4', '#2dd4bf'] },
  { emoji: '📦', gradientColors: ['#f97316', '#fbbf24'] },
  { emoji: '🧺', gradientColors: ['#ec4899', '#fb7185'] },
  { emoji: '📣', gradientColors: ['#ef4444', '#fb923c'] },
  { emoji: '👆', gradientColors: ['#eab308', '#a3e635'] },
  { emoji: '🎯', gradientColors: ['#6366f1', '#60a5fa'] },
];

// ---------------------------------------------------------------------------
// Interactive SVG Line Chart with tooltips
// ---------------------------------------------------------------------------
function LineChart({
  labels,
  datasets,
  formatValue,
}: {
  labels: string[];
  datasets: { data: number[]; color: string; label: string }[];
  formatValue?: (v: number) => string;
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const width = 900;
  const height = 250;
  const paddingLeft = 10;
  const paddingRight = 10;
  const paddingTop = 20;
  const paddingBottom = 24;

  const chartW = width - paddingLeft - paddingRight;
  const chartH = height - paddingTop - paddingBottom;

  const allValues = datasets.flatMap((d) => d.data);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(...allValues, 0);
  const range = maxVal - minVal || 1;

  function toX(i: number) {
    if (labels.length <= 1) return paddingLeft + chartW / 2;
    return paddingLeft + (i / (labels.length - 1)) * chartW;
  }

  function toY(v: number) {
    return paddingTop + chartH - ((v - minVal) / range) * chartH;
  }

  const sliceWidth = labels.length > 1 ? chartW / (labels.length - 1) : chartW;

  const hoverX = hoverIdx !== null ? (toX(hoverIdx) / width) * 100 : 0;

  return (
    <div className="relative w-full" style={{ aspectRatio: `${width}/${height}` }} onMouseLeave={() => setHoverIdx(null)}>
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-full"
      preserveAspectRatio="none"
    >
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
        const y = paddingTop + chartH * (1 - frac);
        return (
          <line
            key={frac}
            x1={paddingLeft}
            y1={y}
            x2={width - paddingRight}
            y2={y}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        );
      })}

      {/* Lines + areas */}
      {datasets.map((ds) => {
        const points = ds.data.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
        const areaPoints = [
          `${toX(0)},${toY(minVal)}`,
          ...ds.data.map((v, i) => `${toX(i)},${toY(v)}`),
          `${toX(ds.data.length - 1)},${toY(minVal)}`,
        ].join(' ');

        return (
          <g key={ds.label}>
            <polygon points={areaPoints} fill={ds.color} opacity={0.08} />
            <polyline
              points={points}
              fill="none"
              stroke={ds.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Show visible dot when there's only one data point */}
            {ds.data.length === 1 && (
              <circle
                cx={toX(0)}
                cy={toY(ds.data[0])}
                r={6}
                fill={ds.color}
                stroke="white"
                strokeWidth={2}
              />
            )}
          </g>
        );
      })}

      {/* Hover dots */}
      {hoverIdx !== null &&
        datasets.map((ds) => (
          <circle
            key={`dot-${ds.label}`}
            cx={toX(hoverIdx)}
            cy={toY(ds.data[hoverIdx] ?? 0)}
            r={4}
            fill={ds.color}
            stroke="white"
            strokeWidth={2}
          />
        ))}

      {/* Vertical hover line */}
      {hoverIdx !== null && (
        <line
          x1={toX(hoverIdx)}
          y1={paddingTop}
          x2={toX(hoverIdx)}
          y2={paddingTop + chartH}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
      )}

      {/* Invisible hover columns */}
      {labels.map((_, i) => (
        <rect
          key={`hover-${i}`}
          x={toX(i) - sliceWidth / 2}
          y={0}
          width={sliceWidth}
          height={height}
          fill="transparent"
          onMouseEnter={() => setHoverIdx(i)}
        />
      ))}

      {/* X labels */}
      {labels.map((label, i) => {
        const step = Math.max(1, Math.floor(labels.length / 10));
        if (i % step !== 0 && i !== labels.length - 1) return null;
        return (
          <text
            key={i}
            x={toX(i)}
            y={height - 4}
            textAnchor="middle"
            fill="rgba(255,255,255,0.4)"
            fontSize={11}
          >
            {label}
          </text>
        );
      })}
    </svg>

    {/* HTML overlay tooltip - fixed pixel size, nie skaluje się z SVG */}
    {hoverIdx !== null && (
      <div
        className="absolute top-0 z-10 pointer-events-none rounded-lg border bg-[rgba(20,20,30,0.95)] border-white/15 px-3 py-2 shadow-lg"
        style={{
          left: `${hoverX}%`,
          transform: `translateX(${hoverX > 80 ? '-100%' : hoverX < 20 ? '0%' : '-50%'})`,
          minWidth: '200px',
          maxWidth: '260px',
        }}
      >
        <div className="text-xs text-white/60 mb-1">{labels[hoverIdx]}</div>
        {datasets.map((ds) => (
          <div
            key={ds.label}
            className="text-sm font-semibold whitespace-nowrap"
            style={{ color: ds.color }}
          >
            {ds.label}: {(formatValue ?? formatPLN)(ds.data[hoverIdx] ?? 0)}
          </div>
        ))}
      </div>
    )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Stacked Bar Chart
// ---------------------------------------------------------------------------
function StackedBarChart({
  labels,
  layers,
}: {
  labels: string[];
  layers: { data: number[]; color: string; label: string }[];
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const totals = labels.map((_, i) => layers.reduce((sum, layer) => sum + (layer.data[i] || 0), 0));
  const maxTotal = Math.max(...totals, 1);

  return (
    <div>
      <div className="relative flex items-end gap-1 h-48 w-full px-1">
        {/* Horizontal grid lines */}
        {[0.25, 0.5, 0.75, 1].map((frac) => (
          <div
            key={frac}
            className="absolute left-0 right-0 border-t border-border-subtle pointer-events-none z-0"
            style={{ bottom: `${frac * 100}%` }}
          />
        ))}
        {labels.map((label, i) => {
          const step = Math.max(1, Math.floor(labels.length / 12));
          const showLabel = i % step === 0 || i === labels.length - 1;

          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center justify-end h-full min-w-0 relative"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            >
              {/* Tooltip */}
              {hoverIdx === i && (
                <div className="chart-tooltip absolute -top-24 left-1/2 -translate-x-1/2 border text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none z-20 whitespace-nowrap">
                  <p className="font-semibold mb-1">{label} — {formatPLN(totals[i])}</p>
                  {layers.map((layer) => {
                    const val = layer.data[i] || 0;
                    if (val === 0) return null;
                    return (
                      <div key={layer.label} className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: layer.color }} />
                        <span className="chart-tooltip-secondary">{layer.label}:</span>
                        <span className="font-medium">{formatPLN(val)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div
                className={`w-full max-w-[28px] flex flex-col-reverse rounded-t overflow-hidden transition-opacity ${hoverIdx === i ? 'opacity-100' : 'opacity-80 hover:opacity-100'}`}
                style={{
                  height: `${(totals[i] / maxTotal) * 100}%`,
                  minHeight: totals[i] > 0 ? '2px' : '0px',
                  cursor: 'pointer',
                }}
              >
                {layers.map((layer) => {
                  const val = layer.data[i] || 0;
                  const pct = totals[i] > 0 ? (val / totals[i]) * 100 : 0;
                  return (
                    <div
                      key={layer.label}
                      style={{
                        height: `${pct}%`,
                        backgroundColor: layer.color,
                      }}
                    />
                  );
                })}
              </div>
              {showLabel && (
                <span className="text-[9px] text-text-muted mt-1 truncate max-w-full">{label}</span>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4">
        {layers.map((layer) => (
          <div key={layer.label} className="flex items-center gap-1.5 text-xs text-text-secondary">
            <span
              className="w-2.5 h-2.5 rounded-sm inline-block"
              style={{ backgroundColor: layer.color }}
            />
            {layer.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Medal component for top 3
// ---------------------------------------------------------------------------
function RankBadge({ rank }: { rank: number }) {
  if (rank === 1)
    return (
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold bg-warning/20 text-warning border border-warning/30">
        1
      </span>
    );
  if (rank === 2)
    return (
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold bg-gray-300/20 text-text-secondary border border-gray-400/30">
        2
      </span>
    );
  if (rank === 3)
    return (
      <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold bg-orange-400/20 text-orange-400 border border-orange-400/30">
        3
      </span>
    );
  return (
    <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold bg-surface-hover/50 text-text-muted">
      {rank}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export function FinanceDashboardPage() {
  const todayPreset = getDatePreset('today');
  const [dateFrom, setDateFrom] = useState(todayPreset.from);
  const [dateTo, setDateTo] = useState(todayPreset.to);
  const [activePreset, setActivePreset] = useState<string>('today');
  const [completedOnly, setCompletedOnly] = useState(false);
  const [codPendingVisible, setCodPendingVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);

  // Store filter
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');
  const [isVat, setIsVat] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Expandable store breakdown — per-store product data
  const [expandedStoreId, setExpandedStoreId] = useState<string | null>(null);
  const [storeProducts, setStoreProducts] = useState<Record<string, TopProduct[]>>({});
  const [storeProductsLoading, setStoreProductsLoading] = useState<string | null>(null);

  // Load stores and config on mount
  useEffect(() => {
    storeApi.getAll().then((res) => {
      const list = ((res.data ?? res) as StoreOption[]) || [];
      setStores(list);
    }).catch(() => { /* silent */ });
    financeApi.getConfig().then((res) => {
      const cfg = (res.data ?? res) as { vatEnabled?: boolean };
      setIsVat(!!cfg.vatEnabled);
    }).catch(() => {});
  }, []);

  const handleToggleStoreExpand = useCallback(async (storeId: string) => {
    if (expandedStoreId === storeId) {
      setExpandedStoreId(null);
      return;
    }
    setExpandedStoreId(storeId);
    // Fetch products for this store if not cached
    if (!storeProducts[storeId]) {
      setStoreProductsLoading(storeId);
      try {
        const res = await financeApi.getTopProducts({ dateFrom, dateTo, storeIds: storeId, limit: 20, completedOnly } as Parameters<typeof financeApi.getTopProducts>[0]);
        setStoreProducts(prev => ({ ...prev, [storeId]: (res.data as TopProduct[]) || [] }));
      } catch {
        setStoreProducts(prev => ({ ...prev, [storeId]: [] }));
      } finally {
        setStoreProductsLoading(null);
      }
    }
  }, [expandedStoreId, storeProducts, dateFrom, dateTo, completedOnly]);

  const handleSyncAll = async () => {
    if (syncing || stores.length === 0) return;
    setSyncing(true);
    try {
      // Sync WooCommerce stores + Facebook ads in parallel
      await Promise.all([
        (async () => { for (const store of stores) { await storeApi.sync(store.id); } })(),
        financeApi.syncFacebookAds().catch(() => {}), // FB sync — ignore errors (rate limit etc.)
      ]);
      await fetchData(dateFrom, dateTo);
    } catch {
      // silent
    } finally {
      setSyncing(false);
    }
  };

  const fetchData = async (from: string, to: string, storeId?: string, completed?: boolean) => {
    setLoading(true);
    setStoreProducts({}); // Reset per-store product cache
    setExpandedStoreId(null);
    try {
      const params: Record<string, string | boolean | undefined> = {
        dateFrom: from,
        dateTo: to,
        completedOnly: completed ?? completedOnly,
      };
      const sid = storeId ?? selectedStoreId;
      if (sid && sid !== 'all') {
        params.storeIds = sid;
      }
      const topParams: Record<string, string | boolean> = { dateFrom: from, dateTo: to, completedOnly: completed ?? completedOnly };
      if (sid && sid !== 'all') {
        topParams.storeIds = sid;
      }
      const [dashRes, topRes] = await Promise.all([
        financeApi.getDashboard(params as Parameters<typeof financeApi.getDashboard>[0]),
        financeApi.getTopProducts(topParams as Parameters<typeof financeApi.getTopProducts>[0]),
      ]);
      setDashboardData(dashRes.data as DashboardData);
      setTopProducts((topRes.data as TopProduct[]) || []);
    } catch (err) {
      console.error('Failed to fetch finance dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    fetchData(dateFrom, dateTo);
  };

  const totals = dashboardData?.totals;
  const chart = dashboardData?.chart;

  // KPI cards data
  const kpiCards = useMemo(() => {
    if (!totals) return [];
    return [
      {
        label: 'Przychód (brutto)',
        value: formatPLN((totals.revenueGross ?? 0) + (totals.shippingGross ?? 0)),
        valueColor: 'text-text-primary',
      },
      {
        label: 'Dochód (zysk brutto)',
        value: formatPLN(totals.grossProfit ?? 0),
        valueColor: 'text-text-primary',
      },
      {
        label: 'Zysk netto',
        value: formatPLN(totals.netProfit ?? 0),
        valueColor: (totals.netProfit ?? 0) >= 0 ? 'text-success' : 'text-danger',
      },
      {
        label: 'Zamówienia',
        value: String(totals.ordersCount ?? 0),
        valueColor: 'text-text-primary',
      },
      {
        label: 'Sprzedane produkty',
        value: String(totals.itemsSold ?? 0),
        valueColor: 'text-text-primary',
      },
      {
        label: 'Średni koszyk',
        value: formatPLN(totals.avgOrder ?? 0),
        valueColor: 'text-text-primary',
      },
      {
        label: 'Wydatki na reklamę',
        value: formatPLN(totals.adSpend ?? 0),
        valueColor: 'text-text-primary',
      },
      {
        label: 'CTR (Unikatowy CTR)',
        value: `${(totals.ctr ?? 0).toFixed(2)}%`,
        valueColor: 'text-text-primary',
      },
      {
        label: 'ROAS',
        value: `${(totals.roas ?? 0).toFixed(1)}x`,
        valueColor: 'text-text-primary',
      },
    ];
  }, [totals, isVat]);

  // Stacked bar chart layers
  const expenseLayers = useMemo(() => {
    if (!chart) return [];
    return [
      { data: chart.costProducts || [], color: '#ef4444', label: `Koszt produktów (${isVat ? 'netto' : 'brutto'})` },
      { data: chart.costShipping || [], color: '#f59e0b', label: `Koszt wysyłki (${isVat ? 'netto' : 'brutto'})` },
      { data: chart.costTransaction || [], color: '#8b5cf6', label: 'Koszt transakcji (brutto)' },
      { data: chart.costFixed || [], color: '#6366f1', label: `Koszty stałe (${isVat ? 'netto' : 'brutto'})` },
      { data: chart.adSpend || [], color: '#ec4899', label: 'Reklama' },
    ];
  }, [chart, isVat]);

  return (
    <div className="space-y-8">
      <PageHeader
        icon={Wallet}
        title="Panel Finansowy"
        subtitle="Przegląd przychodów, kosztów i kluczowych wskaźników"
      />

      {/* Date range picker + filters */}
      <div className="space-y-3 p-4 bg-surface-raised/50 border border-border-subtle rounded-xl">
        {/* Quick date presets */}
        <div className="flex flex-wrap gap-2">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={async () => {
                if (preset.key === 'max') {
                  setActivePreset(preset.key);
                  const to = new Date().toISOString().slice(0, 10);
                  setDateTo(to);
                  try {
                    const res = await financeApi.getAdDateRange();
                    const adFrom = (res.data as { from: string | null })?.from;
                    const from = adFrom || '2020-01-01';
                    setDateFrom(from);
                    fetchData(from, to);
                  } catch {
                    const from = '2020-01-01';
                    setDateFrom(from);
                    fetchData(from, to);
                  }
                  return;
                }
                const { from, to } = getDatePreset(preset.key);
                setDateFrom(from);
                setDateTo(to);
                setActivePreset(preset.key);
                fetchData(from, to);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activePreset === preset.key
                  ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30'
                  : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Store filter badges */}
        {stores.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSelectedStoreId('all');
                fetchData(dateFrom, dateTo, 'all');
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                selectedStoreId === 'all'
                  ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30'
                  : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
              }`}
            >
              Wszystkie sklepy
            </button>
            {stores.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setSelectedStoreId(s.id);
                  fetchData(dateFrom, dateTo, s.id);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectedStoreId === s.id
                    ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30'
                    : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-secondary">Od</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setActivePreset(''); }}
              className="bg-surface-base border border-border rounded-lg px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-secondary">Do</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setActivePreset(''); }}
              className="bg-surface-base border border-border rounded-lg px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
            />
          </div>

          {/* Oczekujące COD toggle (only when codPendingEnabled in settings) */}
          {dashboardData?.codPending?.enabled && (
            <button
              type="button"
              onClick={() => setCodPendingVisible(v => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                codPendingVisible
                  ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                  : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary'
              }`}
              title="Pokaż szacunkową wartość pobrań oczekujących na rozliczenie"
            >
              <div className={`relative w-8 h-4 rounded-full transition-colors ${codPendingVisible ? 'bg-accent-primary' : 'bg-surface-muted'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${codPendingVisible ? 'left-4' : 'left-0.5'}`} />
              </div>
              Oczekujące COD
            </button>
          )}

          <FinanceResultModeToggle
            completedOnly={completedOnly}
            onChange={(next) => {
              setCompletedOnly(next);
              fetchData(dateFrom, dateTo, undefined, next);
            }}
          />

          <button
            onClick={handleSyncAll}
            disabled={syncing || stores.length === 0}
            className="px-3 py-2 rounded-lg bg-success/10 text-success text-sm border border-success/20 hover:bg-success/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <svg className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {syncing ? 'Synchronizuję...' : 'Synchronizuj'}
          </button>

          <button
            onClick={handleFilter}
            disabled={loading}
            className="px-5 py-2 bg-accent-primary/10 text-accent-primary border border-accent-primary/30 rounded-lg text-sm font-medium hover:bg-accent-primary/30 transition-colors disabled:opacity-50"
          >
            {loading ? 'Ładowanie...' : 'Filtruj'}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-accent-primary/30 border-t-neon-cyan rounded-full animate-spin" />
        </div>
      )}

      {/* Content - shown when not loading */}
      {!loading && dashboardData && (
        <>
          {/* KPI Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {kpiCards.map((card, index) => {
              const cfg = KPI_CONFIG[index] || KPI_CONFIG[0];
              return (
                <div
                  key={card.label}
                  className="relative bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden hover:border-border transition-all duration-300 animate-slide-up group"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {/* Gradient top bar */}
                  <div
                    className="h-1"
                    style={{
                      background: `linear-gradient(90deg, ${cfg.gradientColors[0]}, ${cfg.gradientColors[1]})`,
                    }}
                  />

                  {/* Large emoji in background — overflows beyond card bounds, clipped by overflow-hidden */}
                  <div className="absolute -right-3 bottom-1/2 translate-y-1/2 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity pointer-events-none select-none leading-none">
                    <span className="text-[6.5rem]">{cfg.emoji}</span>
                  </div>

                  {/* Card content */}
                  <div className="p-5 flex items-center gap-4 relative z-10">
                    {/* Emoji icon */}
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-surface-hover/50 text-2xl">
                      {cfg.emoji}
                    </div>

                    {/* Text */}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-text-secondary mb-0.5 truncate">{card.label}</p>
                      <p className={`text-xl font-bold ${card.valueColor} truncate`}>{card.value}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* COD Pending cards (50/50) — only when enabled in settings + toggle ON */}
          {dashboardData?.codPending?.enabled && codPendingVisible && dashboardData.codPending.delivered && dashboardData.codPending.all && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-slide-up">
              {/* Kafelek A — Oczekuje wypłaty (pewne) */}
              <div className="relative bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden hover:border-border transition-all duration-300 group">
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #10b981, #059669)' }} />
                <div className="absolute -right-3 bottom-1/2 translate-y-1/2 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity pointer-events-none select-none leading-none">
                  <span className="text-[6.5rem]">💰</span>
                </div>
                <div className="p-5 relative z-10">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-surface-hover/50 text-2xl">
                      💰
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-text-secondary mb-0.5">
                        Oczekuje wypłaty <span className="text-success">(pewne)</span>
                      </p>
                      <p className="text-xl font-bold text-text-primary truncate">
                        {formatPLN(dashboardData.codPending.delivered.amount)}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-text-muted">
                    {dashboardData.codPending.delivered.count} paczek dostarczonych, czeka na przelew od przewoźnika.
                  </p>
                </div>
              </div>

              {/* Kafelek B — Potencjał COD (szacunkowe) */}
              <div className="relative bg-surface-raised/50 border border-border-subtle rounded-xl overflow-hidden hover:border-border transition-all duration-300 group">
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706)' }} />
                <div className="absolute -right-3 bottom-1/2 translate-y-1/2 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity pointer-events-none select-none leading-none">
                  <span className="text-[6.5rem]">📦</span>
                </div>
                <div className="p-5 relative z-10">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-surface-hover/50 text-2xl">
                      📦
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-text-secondary mb-0.5">
                        Potencjał COD <span className="text-warning">(szacunek)</span>
                      </p>
                      <p className="text-xl font-bold text-text-primary truncate">
                        {formatPLN(dashboardData.codPending.all.amount)}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-text-muted">
                    {dashboardData.codPending.all.count} zamówień w realizacji
                    {completedOnly ? ' (tylko wysłane paczki)' : ' (wliczając zamówienia przed wysyłką - klient może zrezygnować)'}.
                  </p>
                </div>
              </div>

              {/* Disclaimer pod kafelkami */}
              <div className="md:col-span-2 rounded-xl bg-accent-primary/5 border border-accent-primary/20 p-3">
                <p className="text-xs text-text-secondary">
                  Wartości szacunkowe. Nie uwzględniają rzeczywistego harmonogramu rozliczeń przewoźnika,
                  niepodjętych paczek ani zwrotów w transporcie. Zawsze porównuj z wpływami na konto
                  bankowe. Parametry (dni rozliczenia, transport) zmienisz w
                  {' '}<span className="text-text-primary">Ustawieniach - Rozliczenia COD</span>.
                </p>
              </div>
            </div>
          )}

          {/* Store breakdown (when "All stores" and multiple stores) */}
          {dashboardData.storeBreakdown && dashboardData.storeBreakdown.length > 1 && selectedStoreId === 'all' && (
            <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-5 animate-slide-up">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Udziały sklepów</h2>
              <div className="space-y-3">
                {dashboardData.storeBreakdown
                  // Pomijaj sklepy bez adspend ORAZ bez revenue — nic do pokazania.
                  .filter(s => (s.revenueTotalGross ?? ((s.revenueGross ?? 0) + (s.shippingGross ?? 0))) > 0 || s.adSpend > 0)
                  // Sortowanie: sklepy ze sprzedażą (by percentage desc), potem sklepy tylko z adspend
                  // (by adSpend desc) — żeby problematyczne były widoczne, nie zakopane.
                  .sort((a, b) => {
                    const aRevenueTotal = a.revenueTotalGross ?? ((a.revenueGross ?? 0) + (a.shippingGross ?? 0));
                    const bRevenueTotal = b.revenueTotalGross ?? ((b.revenueGross ?? 0) + (b.shippingGross ?? 0));
                    const aLoss = aRevenueTotal === 0 && a.adSpend > 0;
                    const bLoss = bRevenueTotal === 0 && b.adSpend > 0;
                    if (aLoss !== bLoss) return aLoss ? 1 : -1;
                    if (aLoss && bLoss) return b.adSpend - a.adSpend;
                    return b.percentage - a.percentage;
                  })
                  .map((store) => {
                    const isExpanded = expandedStoreId === store.storeId;
                    const products = storeProducts[store.storeId] || [];
                    const isLoadingProducts = storeProductsLoading === store.storeId;
                    const storeRevenueTotal = store.revenueTotalGross ?? ((store.revenueGross ?? 0) + (store.shippingGross ?? 0));
                    // Sklep „tylko reklama" — adSpend > 0 bez sprzedaży. Pokazuj na czerwono.
                    const isAdOnly = storeRevenueTotal === 0 && store.adSpend > 0;

                    return (
                  <div
                    key={store.storeId}
                    className={`rounded-xl overflow-hidden border ${
                      isAdOnly
                        ? 'bg-danger/5 border-danger/30'
                        : 'bg-surface-hover/30 border-border-subtle'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleStoreExpand(store.storeId)}
                      className="w-full p-4 text-left hover:bg-surface-hover/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-base">🏪</span>
                          <span className="text-sm font-medium text-text-primary truncate">{store.storeName}</span>
                          {isAdOnly && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-danger/15 text-danger border border-danger/30 shrink-0">
                              Tylko reklama - brak sprzedaży
                            </span>
                          )}
                          <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                        <span className={`text-lg font-bold ${isAdOnly ? 'text-danger' : 'text-accent-primary'}`}>
                          {isAdOnly ? `-${formatPLN(store.adSpend)}` : `${store.percentage}%`}
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full h-2 bg-surface-muted rounded-full overflow-hidden mb-3">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isAdOnly
                              ? 'bg-gradient-to-r from-danger to-red-700'
                              : 'bg-gradient-to-r from-accent-primary to-accent-info'
                          }`}
                          style={{ width: isAdOnly ? '100%' : `${Math.min(100, store.percentage)}%` }}
                        />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3 text-xs">
                        <div>
                          <span className="text-text-muted">Zamówienia</span>
                          <p className="text-text-primary font-medium">{store.ordersCount}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">Przychód razem</span>
                          <p className="text-text-primary font-medium">{formatPLN(storeRevenueTotal)}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">Produkty</span>
                          <p className="text-text-primary font-medium">{formatPLN(store.revenueGross ?? 0)}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">Przesyłka</span>
                          <p className="text-text-primary font-medium">{formatPLN(store.shippingGross ?? 0)}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">Zysk brutto</span>
                          <p className={`font-medium ${store.grossProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatPLN(store.grossProfit)}
                          </p>
                        </div>
                        <div>
                          <span className="text-text-muted">Reklama</span>
                          <p className="text-text-primary font-medium">{formatPLN(store.adSpend)}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">ROAS</span>
                          <p className={`font-medium ${store.roas >= 1 ? 'text-success' : store.roas > 0 ? 'text-warning' : 'text-text-muted'}`}>
                            {store.adSpend > 0 ? `${store.roas.toFixed(1)}x` : '—'}
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* Expanded: per-product breakdown */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-border-subtle">
                        {isLoadingProducts ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="w-5 h-5 border-2 border-accent-primary/30 border-t-neon-cyan rounded-full animate-spin" />
                          </div>
                        ) : products.length === 0 ? (
                          <p className="text-xs text-text-muted py-4 text-center">Brak danych o produktach</p>
                        ) : (
                          <div className="space-y-2 pt-3">
                            {[...products].sort((a, b) => ((b.revenue ?? 0) + (b.adSpend ?? 0)) - ((a.revenue ?? 0) + (a.adSpend ?? 0))).map((product: TopProduct) => {
                              const pctOfStore = store.revenueGross > 0 ? (product.revenue / store.revenueGross) * 100 : 0;
                              const hasChildren = Array.isArray(product.children) && (product.children?.length ?? 0) > 1;

                              return (
                                <div key={product.id} className="rounded-lg border border-border-subtle">
                                  <div className="p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs text-text-primary font-medium truncate flex-1 min-w-0">
                                        {product.name}
                                        {product.isAdditionalSale && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-success/10 border border-success/20 text-[10px] text-success font-medium" title="Produkt dodany jako upsell, downsell, cross-sell lub bump">Dosprzedaż</span>}
                                        {hasChildren && (
                                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-accent-primary/10 border border-accent-primary/20 text-[10px] text-accent-primary font-medium">
                                            Scalone: {(product.children?.length ?? 0)}
                                          </span>
                                        )}
                                      </span>
                                      <span className="text-lg font-bold text-success ml-2 shrink-0">{pctOfStore.toFixed(1)}%</span>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="w-full h-2 bg-surface-muted rounded-full overflow-hidden mb-3">
                                      <div
                                        className="h-full rounded-full bg-gradient-to-r from-success to-emerald-600 transition-all duration-500"
                                        style={{ width: `${Math.min(100, pctOfStore)}%` }}
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-7 gap-3 text-xs">
                                      <div>
                                        <span className="text-text-muted">Zamówienia</span>
                                        <p className="text-text-primary font-medium">{product.ordersCount ?? 0}</p>
                                      </div>
                                      <div>
                                        <span className="text-text-muted">Ilość</span>
                                        <p className="text-text-primary font-medium">{product.qty} szt.</p>
                                      </div>
                                      <div>
                                        <span className="text-text-muted">Przychód produktu</span>
                                        <p className="text-text-primary font-medium">{formatPLN(product.revenue)}</p>
                                      </div>
                                      <div>
                                        <span className="text-text-muted">Zysk brutto</span>
                                        <p className={`font-medium ${(product.grossProfit ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                          {formatPLN(product.grossProfit ?? 0)}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-text-muted">Zysk netto</span>
                                        <p className={`font-medium ${(product.netProfit ?? product.grossProfit ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                          {formatPLN(product.netProfit ?? product.grossProfit ?? 0)}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-text-muted">Reklama</span>
                                        <p className="text-text-primary font-medium">{(product.adSpend ?? 0) > 0 ? formatPLN(product.adSpend) : '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-text-muted">ROAS</span>
                                        <p className={`font-medium ${(product.roas ?? 0) >= 1 ? 'text-success' : (product.roas ?? 0) > 0 ? 'text-warning' : 'text-text-muted'}`}>
                                          {(product.adSpend ?? 0) > 0 ? `${product.roas.toFixed(1)}x` : '—'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Sub-products (landing pages) for merged groups */}
                                  {hasChildren && (
                                    <div className="px-3 pb-3 space-y-1.5 border-t border-border-subtle pt-2">
                                      {(product.children ?? []).map(child => (
                                        <div key={child.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs py-2 px-2 rounded bg-surface-base/30">
                                          <span className="text-text-muted shrink-0">↳</span>
                                          <div className="min-w-0 flex-1 basis-60"><p className="truncate text-text-secondary" title={child.name}>{child.name}</p>
                                            <span className="mt-1 inline-flex max-w-full flex-wrap gap-x-2 rounded-md border border-border-subtle bg-surface-hover/50 px-2 py-1 text-xs text-text-muted">
                                              <span>ID: <strong className="font-medium text-text-secondary">{child.externalProductId || 'Brak ID'}</strong></span>
                                              <span>SKU: <strong className="break-all font-medium text-text-secondary">{child.sku || 'Brak SKU'}</strong></span>
                                            </span>
                                          </div>
                                          <span className="text-text-secondary shrink-0">{child.ordersCount ?? 0} zam.</span>
                                          <span className="text-text-secondary shrink-0">{child.qty} szt.</span>
                                          <span className="text-text-primary font-medium shrink-0">{formatPLN(child.revenue)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Charts row: Revenue & Profit + Daily Orders */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Line chart: Przychód i Zysk */}
            <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div><h2 className="text-lg font-semibold text-text-primary">Przychód i zysk</h2>
                  {chart?.granularity === 'hour' && <p className="mt-1 text-xs text-text-muted">Godzinowo · czas Warszawy</p>}</div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 rounded bg-accent-primary inline-block" />
                    <span className="text-text-secondary">Przychód</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 rounded bg-success inline-block" />
                    <span className="text-text-secondary">Zysk</span>
                  </span>
                </div>
              </div>
              <div>
                {chart && chart.labels.length > 0 ? (
                  <LineChart
                    labels={chart.labels}
                    datasets={[
                      { data: chart.revenue, color: '#4a9eff', label: 'Przychód' },
                      { data: chart.profit, color: '#10b981', label: 'Zysk' },
                    ]}
                  />
                ) : (
                  <EmptyState
                    icon={TrendingUp}
                    title="Brak danych do wyświetlenia"
                    size="sm"
                  />
                )}
              </div>
              {chart?.profitAllocation === 'REVENUE_SHARE_ESTIMATE' && <p className="mt-3 text-xs leading-5 text-text-muted">Zysk godzinowy jest szacunkiem: wynik dnia dzielimy proporcjonalnie do przychodu w danej godzinie. Przy braku przychodu dzielimy go równo. Koszty reklam są dostępne dziennie.</p>}
            </div>

            {/* Line chart: Zamówienia dziennie */}
            <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-text-primary">{chart?.granularity === 'hour' ? 'Zamówienia godzinowo' : 'Zamówienia dziennie'}</h2>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 rounded bg-accent-primary inline-block" />
                    <span className="text-text-secondary">Zamówienia</span>
                  </span>
                </div>
              </div>
              <div>
                {chart && chart.labels.length > 0 && chart.orders && chart.orders.length > 0 ? (
                  <LineChart
                    labels={chart.labels}
                    datasets={[
                      { data: chart.orders, color: '#4a9eff', label: 'Zamówienia' },
                    ]}
                    formatValue={(v) => `${v} szt.`}
                  />
                ) : (
                  <EmptyState
                    icon={BarChart2}
                    title="Brak danych do wyświetlenia"
                    size="sm"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Expenses: stacked chart (multi-day) or summary cards (single-day hourly) */}
          {dateFrom === dateTo ? (
            /* Single-day mode: show expense summary cards since hourly cost data isn't available */
            <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-5">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Wydatki dnia</h2>
              {totals ? (() => {
                const expenses = [
                  { label: 'Koszt produktów', value: totals.costProducts ?? 0, color: '#ef4444' },
                  { label: 'Koszt wysyłki', value: totals.costShipping ?? 0, color: '#f59e0b' },
                  ...((totals.costReturns ?? 0) > 0 ? [{ label: 'Zwroty paczek', value: totals.costReturns ?? 0, color: '#f97316' }] : []),
                  { label: 'Prowizje', value: totals.costFees ?? 0, color: '#8b5cf6' },
                  { label: 'Koszty stałe', value: totals.costFixed ?? 0, color: '#6366f1' },
                  { label: 'Reklama', value: totals.adSpend, color: '#ec4899' },
                ];
                const totalExpenses = expenses.reduce((sum, e) => sum + e.value, 0);
                const sortedExpenses = [...expenses].sort((a, b) => b.value - a.value);

                return (
                  <div className="space-y-3">
                    {sortedExpenses.map((exp) => {
                      const pct = totalExpenses > 0 ? (exp.value / totalExpenses) * 100 : 0;
                      return (
                        <div key={exp.label}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-text-secondary">{exp.label}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-text-muted">{pct.toFixed(1)}%</span>
                              <span className="text-sm font-medium text-text-primary w-24 text-right">{formatPLN(exp.value)}</span>
                            </div>
                          </div>
                          <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(100, pct)}%`, backgroundColor: exp.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
                      <span className="text-sm font-semibold text-text-secondary">Razem wydatki</span>
                      <span className="text-sm font-bold text-text-primary">{formatPLN(totalExpenses)}</span>
                    </div>
                  </div>
                );
              })() : (
                <EmptyState
                  icon={BarChart2}
                  title="Brak danych do wyświetlenia"
                  size="sm"
                />
              )}
            </div>
          ) : (
            /* Multi-day: standard stacked bar chart */
            <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-5">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Wszystkie wydatki</h2>
              <div>
                {chart && chart.labels.length > 0 ? (
                  <StackedBarChart labels={chart.labels} layers={expenseLayers} />
                ) : (
                  <EmptyState
                    icon={BarChart2}
                    title="Brak danych do wyświetlenia"
                    size="sm"
                  />
                )}
              </div>
            </div>
          )}

          {/* Top 10 products */}
          <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-5">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Top 10 produktów</h2>
            {topProducts.length > 0 ? (
              <div className="space-y-2">
                {topProducts.slice(0, 10).map((product, index) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-hover/30 hover:bg-surface-hover/50 transition-colors"
                  >
                    <RankBadge rank={index + 1} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary truncate">{product.name} {product.isAdditionalSale && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-success/10 border border-success/20 text-[10px] text-success font-medium">Dosprzedaż</span>}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium text-text-primary">{formatPLN(product.revenue)}</p>
                      <p className="text-xs text-text-secondary">
                        {product.qty} szt. ({product.ordersCount ?? 0} zam.)
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Package}
                title="Brak danych o produktach"
                size="sm"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
